import { parseCookies } from '~/utils/cookies.client';
import { detectLocale, defaultLocale } from '~/utils/language';
import { createRoutePath } from '~/utils/routePath';
import { getDefaultChannel } from '~/utils/runtimeConfig';

/**
 * Parse cookie header string (server-side only).
 * Inlined here to avoid importing from server module.
 */
function parseCookieHeader(cookieHeader: string | null): Record<string, string> {
  if (!cookieHeader) return {};
  return Object.fromEntries(
    cookieHeader
      .split(';')
      .map((c) => c.trim().split('='))
      .filter(([k]) => k)
      .map(([k, v]) => [k, decodeURIComponent(v ?? '')])
  );
}

/**
 * Flag to prevent infinite refresh loops
 */
let isRefreshing = false;

/**
 * Attempt to refresh JWT token using refresh_token cookie.
 * Returns true if refresh was successful.
 *
 * Browser-only: the refresh_token cookie is path-scoped to the refresh endpoint on the
 * API domain, so the Remix server never receives it and cannot refresh on the user's
 * behalf — every recovery from an expired BEARER has to happen here, in the browser.
 */
export async function refreshAuthToken(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (isRefreshing) return false;

  isRefreshing = true;
  try {
    const response = await fetch(`${window.ENV?.API_URL}/api/v2/shop/token/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    isRefreshing = false;
  }
}

interface ApiFetchContext {
  request?: Request;
  skipAuthRedirect?: boolean;
}

export async function apiFetch(url: string, init: RequestInit = {}, context?: ApiFetchContext) {
  const headers = new Headers(init.headers);
  const isServer = typeof document === 'undefined';

  let locale = defaultLocale;
  let channelCode = getDefaultChannel();

  // -------------------------
  // SSR
  // -------------------------
  if (isServer) {
    const request = context?.request;

    // 2️⃣ Currency from cookies (SSR)
    const cookies = parseCookieHeader(request?.headers.get('cookie') ?? null);

    // 1️⃣ Locale from URL (SSR) - use channel's default locale from cookie
    if (request) {
      const channelDefaultLocale = cookies.channelDefaultLocale ?? defaultLocale;
      locale = detectLocale(request, channelDefaultLocale);
    }

    if (cookies.channelCode) {
      channelCode = cookies.channelCode;
    }

    headers.set('Accept-Language', locale);
    headers.set('Accept-Channel', channelCode);

    // Předat cookies do API requestu (credentials: 'include' nefunguje na serveru)
    const cookieHeader = request?.headers.get('cookie');
    if (cookieHeader) {
      headers.set('Cookie', cookieHeader);
    }

    return fetch(url, {
      ...init,
      headers,
    });
  }

  // -------------------------
  // CSR (browser)
  // -------------------------

  // 2️⃣ Currency from cookies (CSR)
  const cookies = parseCookies();

  // 1️⃣ Locale from URL (CSR) - use channel's default locale from cookie
  const channelDefaultLocale = cookies.channelDefaultLocale ?? defaultLocale;
  locale = detectLocale(window.location.pathname, channelDefaultLocale);

  if (cookies.channelCode) {
    channelCode = cookies.channelCode;
  }

  headers.set('Accept-Language', locale);
  headers.set('Accept-Channel', channelCode);

  const response = await fetch(url, {
    ...init,
    headers,
    credentials: 'include',
  });

  // Handle 401 - attempt token refresh and retry (skip for auth endpoints)
  if (response.status === 401 && !context?.skipAuthRedirect) {
    const refreshSuccess = await refreshAuthToken();

    if (refreshSuccess) {
      // Retry original request with new token (cookie set automatically)
      return fetch(url, {
        ...init,
        headers,
        credentials: 'include',
      });
    }

    // Refresh failed - clear auth state and redirect to login
    localStorage.removeItem('userUrl');
    const routePath = createRoutePath(locale, channelDefaultLocale);
    window.location.href = routePath('login');
  }

  return response;
}
